// Pomocnicze dla dokumentów do druku: daty, cena słownie, walidacja (ostrzeżenia, nie blokada).
// Bez lookbehind w wyrażeniach regularnych (Safari < 16.4).

export function trim (s) {
    return s == null ? '' : String(s).trim()
}

export function esc (s) {
    return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
}

// 2026-10-27 → 27.10.2026
export function fmtDate (iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
    return m ? `${m[3]}.${m[2]}.${m[1]}` : trim(iso)
}

export function todayIso (d = new Date()) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function groups (s) {
    return String(s).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

export function digits (s) {
    return String(s || '').replace(/[\s-]/g, '')
}

// cena słownie (do 999 999 999 zł); odmiana: 1 — złoty, 2–4 (bez 12–14) — złote, reszta — złotych
const U = ['', 'jeden', 'dwa', 'trzy', 'cztery', 'pięć', 'sześć', 'siedem', 'osiem', 'dziewięć']
const NAS = ['dziesięć', 'jedenaście', 'dwanaście', 'trzynaście', 'czternaście', 'piętnaście', 'szesnaście', 'siedemnaście', 'osiemnaście', 'dziewiętnaście']
const DZ = ['', '', 'dwadzieścia', 'trzydzieści', 'czterdzieści', 'pięćdziesiąt', 'sześćdziesiąt', 'siedemdziesiąt', 'osiemdziesiąt', 'dziewięćdziesiąt']
const SE = ['', 'sto', 'dwieście', 'trzysta', 'czterysta', 'pięćset', 'sześćset', 'siedemset', 'osiemset', 'dziewięćset']
const GR = [null, ['tysiąc', 'tysiące', 'tysięcy'], ['milion', 'miliony', 'milionów']]

export function odm (n, forms) {
    if (n === 1) return forms[0]
    const a = n % 10, b = n % 100
    return a >= 2 && a <= 4 && (b < 12 || b > 14) ? forms[1] : forms[2]
}

function trojka (n) {
    const r = n % 100, out = [SE[Math.floor(n / 100)]]
    if (r >= 10 && r < 20) out.push(NAS[r - 10])
    else out.push(DZ[Math.floor(r / 10)], U[r % 10])
    return out.filter(Boolean).join(' ')
}

export function slownie (n) {
    if (n === 0) return 'zero'
    const parts = []
    let g = 0
    while (n > 0) {
        const c = n % 1000
        if (c) parts.unshift(g && c === 1 ? GR[g][0] : trojka(c) + (g ? ' ' + odm(c, GR[g]) : ''))
        n = Math.floor(n / 1000)
        g++
    }
    return parts.join(' ')
}

// „38 500”, „38500,5” → { zl: 38500, gr: '50' }
export function parsePrice (s) {
    const m = /^(\d{1,9})(?:[.,](\d{1,2}))?$/.exec(digits(s))
    return m ? { zl: +m[1], gr: m[2] ? (m[2] + '0').slice(0, 2) : '' } : null
}

export function formatPrice (s) {
    const p = parsePrice(s)
    return p ? groups(String(p.zl)) + (p.gr ? ',' + p.gr : '') : s
}

function peselOk (d) {
    const w = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3]
    let s = 0
    for (let i = 0; i < 10; i++) s += w[i] * +d[i]
    return (10 - s % 10) % 10 === +d[10]
}

function nipOk (d) {
    const w = [6, 5, 7, 2, 3, 4, 5, 6, 7]
    let s = 0
    for (let i = 0; i < 9; i++) s += w[i] * +d[i]
    return s % 11 === +d[9]
}

function regonOk (d) {
    const w = d.length === 9 ? [8, 9, 2, 3, 4, 5, 6, 7] : [2, 4, 8, 5, 0, 9, 7, 3, 6, 1, 2, 4, 8]
    let s = 0
    for (let i = 0; i < w.length; i++) s += w[i] * +d[i]
    return (s % 11) % 10 === +d[w.length]
}

// walidatory: zwracają treść ostrzeżenia albo '' / undefined
export const VALIDATORS = {
    vin (v) {
        v = digits(v).toUpperCase()
        if (v.length !== 17) return `VIN ma 17 znaków, wpisano ${v.length}.`
        if (/[IOQ]/.test(v)) return 'VIN nie zawiera liter I, O ani Q — sprawdź, czy to nie cyfry 1 i 0.'
        if (!/^[A-Z0-9]+$/.test(v)) return 'VIN składa się tylko z liter i cyfr.'
    },
    peselnip (v) {
        const d = digits(v)
        if (!/^\d+$/.test(d)) return 'PESEL i NIP składają się z cyfr.'
        if (d.length === 11) return peselOk(d) ? '' : 'Sprawdź PESEL — cyfra kontrolna się nie zgadza.'
        if (d.length === 10) return nipOk(d) ? '' : 'Sprawdź NIP — cyfra kontrolna się nie zgadza.'
        return 'PESEL ma 11 cyfr, NIP — 10.'
    },
    peselregon (v) {
        const d = digits(v)
        if (!/^\d+$/.test(d)) return 'PESEL i REGON składają się z cyfr.'
        if (d.length === 11) return peselOk(d) ? '' : 'Sprawdź PESEL — cyfra kontrolna się nie zgadza.'
        if (d.length === 9 || d.length === 14) return regonOk(d) ? '' : 'Sprawdź REGON — cyfra kontrolna się nie zgadza.'
        return 'PESEL ma 11 cyfr, REGON — 9 albo 14.'
    },
    zip (v) {
        if (!/^\d{2}-\d{3}$/.test(trim(v))) return 'Kod pocztowy w formacie 00-000.'
    },
    addr (v) {
        if (/(^|\D)\d{5}(\D|$)/.test(v) && !/\d{2}-\d{3}/.test(v)) return 'Kod pocztowy wpisz w formacie 00-000.'
    },
    reg (v) {
        if (!/^[A-Z]{1,3} ?[A-Z0-9]{3,5}$/.test(trim(v).toUpperCase())) return 'Nietypowy numer rejestracyjny — sprawdź (polskie tablice: 1–3 litery, potem 3–5 znaków).'
    },
    year (v) {
        const y = +v, max = new Date().getFullYear() + 1
        if (!/^\d{4}$/.test(trim(v)) || y < 1900 || y > max) return `Rok w formacie RRRR, od 1900 do ${max}.`
    },
    phone (v) {
        if (!/^(\+?48)?\d{9}$/.test(digits(v))) return 'Numer telefonu ma zwykle 9 cyfr.'
    },
    num (v) {
        if (!/^\d+$/.test(digits(v))) return 'Wpisz same cyfry.'
    },
    price (v) {
        if (!parsePrice(v)) return 'Cena w złotych, np. 38 500 albo 38 500,50.'
    },
}

export function validate (kind, value) {
    const v = trim(value)
    return v && VALIDATORS[kind] ? VALIDATORS[kind](v) || '' : ''
}
